import { useState, useEffect, useMemo, useImperativeHandle, forwardRef, useCallback, useRef } from 'react';
import { Box, Button, Stack, Typography, Alert, DialogContentText, CircularProgress, Paper } from '@mui/material';

import useModalState from '../hooks/useModalState';
import { UnifiedAccount } from '../types/Account.interface';
import WhyImportantLink from '../components/WhyImportantLink';
import { ACCOUNTING_SETUP_HELP_CONTEXT } from '../hooks/useOpenAccountingSetupHelp';

import TransactionSelectionModal from './components/TransactionSelectionModal';
import AccountReviewCard from './components/AccountReviewCard';
import AccountSummary from './components/AccountSummary';
import NoTransactionsCheckbox from './components/NoTransactionsCheckbox';
import useImportTransactionsState from './hooks/useImportTransactionsState';
import usePersistentTransactionSelection from './hooks/usePersistentTransactionSelection';
import useUnifiedAccounts from './hooks/useUnifiedAccounts';

import useEventTracking from 'modules/common/components/AnalyticsTracking/useEventTracking';
import ConfirmationDialog from 'modules/common/components/MUI/ConfirmationDialog';
import BankIcon from 'modules/common/components/Icons/BankIcon';
import { useMember } from 'modules/common/queries/member';
import collectiveApi from 'modules/common/collectiveApi';
import { moneyFormat } from 'modules/common/display';
import useUpdateOnboardingStep from 'services/bookkeeping/mutations/useUpdateOnboardingStep';
import useCompleteOnboardingStep from 'services/bookkeeping/mutations/useCompleteOnboardingStep';
import theme from 'theme';
import useTransactionHistoryStart from 'modules/Onboarding/AccountingSetup/Banking/hooks/useTransactionHistoryStart';
import useStatsigGate from 'modules/common/useStatsigGate';
import usePersistData from 'modules/common/hooks/usePersistData';
import { FeatureGates } from 'modules/common/constants/featuregates';

const WAIVED_ACCOUNTS_KEY = 'import-transactions-waived-accounts';

export interface ImportTransactionsRef {
    handleStepExit: () => Promise<boolean>;
    getTransactionCount: () => number;
    hasSelections: () => boolean;
    canComplete: () => boolean;
    openConfirmationModal: () => void;
}

interface ImportTransactionsProps {
    setCanProceed: (canProceed: boolean) => void;
    onNavigateToNext?: () => void;
    /** For the wizard footer, which can't read this step's state on its own. */
    onCanCompleteChange?: (canComplete: boolean) => void;
    onboardingProgress?: {
        transaction_import_data?: {
            source_accounts?: Array<{
                id: string;
                name: string;
                type: string;
                lastFour: string;
                institution: string;
                status: string;
                plaidAccountId?: string;
            }>;
        };
        bank_connection_data?: {
            confirmed_personal_accounts?: Array<{
                id: string;
                name: string;
                type: string;
                mask: string;
                institution_name: string;
            }>;
        };
    };
}

const ImportTransactions = forwardRef<ImportTransactionsRef, ImportTransactionsProps>(
    ({ onboardingProgress = undefined, setCanProceed, onNavigateToNext, onCanCompleteChange }, ref) => {
        const isRedesignV1 = useStatsigGate(FeatureGates.AccountingSetupRedesignV1);
        const { data: member } = useMember();
        const { formattedDate: transactionStartDate } = useTransactionHistoryStart();
        const { sendTrackingEvent } = useEventTracking();

        // ✅ Phase 3: Separate business logic and modal state
        const { state, setSelectedAccountForTransaction, setError } = useImportTransactionsState();

        // ✅ Phase 3: Separate modal state management
        const { modalState, setModalState } = useModalState();

        // Loading state for confirm button
        const [isConfirming, setIsConfirming] = useState(false);

        // Error state for backend operations
        const [backendError, setBackendError] = useState<string | null>(null);

        // State for checkbox
        const [noTransactionsCheckbox, setNoTransactionsCheckbox] = useState(false);
        const { getData: getWaivedAccountIds, setData: setStoredWaivedAccountIds } = usePersistData<string[]>();
        const [waivedAccountIds, setWaivedAccountIds] = useState<ReadonlySet<string>>(
            () => new Set(getWaivedAccountIds(WAIVED_ACCOUNTS_KEY) ?? [])
        );
        const [showUnresolved, setShowUnresolved] = useState(false);
        // Refs, not state: the wizard's exit call after navigating runs a closure from before the import.
        const isCompletingRef = useRef(false);
        const hasCompletedRef = useRef(false);

        // Use persistent transaction selection
        const {
            hasSelections,
            clearAllSelections,
            clearAccountSelections,
            getAccountSelections,
            setAccountSelections,
            selections,
        } = usePersistentTransactionSelection();

        // Calculate values directly from selections state to avoid timing issues
        const totalSelectionCount = useMemo(() => {
            const count = Object.values(selections).reduce((total, transactions) => total + transactions.length, 0);
            return count;
        }, [selections]);

        // Track categorization section started -- once per mount; `onboardingProgress` changes on every refetch
        const hasTrackedSectionStarted = useRef(false);
        useEffect(() => {
            if (hasTrackedSectionStarted.current) {
                return;
            }

            hasTrackedSectionStarted.current = true;
            sendTrackingEvent('Categorization Section Started', {
                category: 'accounting_setup',
                action: 'section_started',
                stepName: 'import_transactions',
                userType: member?.usertype,
                hasPersonalAccounts:
                    (onboardingProgress?.bank_connection_data?.confirmed_personal_accounts?.length ?? 0) > 0,
            });
        }, [sendTrackingEvent, member?.usertype, onboardingProgress]);

        const totalNetExpenses = useMemo(() => {
            const total = Object.values(selections).reduce((sum, transactions) => {
                return (
                    sum +
                    transactions.reduce((accountSum, transaction) => {
                        const amount = parseFloat(transaction.amount) || 0;
                        return accountSum + Math.abs(amount);
                    }, 0)
                );
            }, 0);
            return total;
        }, [selections]);

        const estimatedDeductions = useMemo(() => {
            const taxRate = 0.22;
            const deductions = totalNetExpenses * taxRate;
            return deductions;
        }, [totalNetExpenses]);

        // Use unified data flow
        const {
            unifiedAccounts,
            stepData,
            isLoading,
            error: accountsError,
            getAccountById,
            invalidateQueries,
            validationWarnings,
        } = useUnifiedAccounts({
            memberId: member?.id,
            onboardingProgress,
            newlyConnectedAccounts: state.newlyConnectedAccounts,
        });

        // Reconcile persisted selections against the live account set.
        // Accounts can be disconnected after their transactions were selected (e.g. a
        // member swaps a personal account for a business one). Those selections linger
        // in localStorage, inflate the count, hide the "no transactions" checkbox, and
        // trap the member with no way to proceed. Drop selections for accounts that no
        // longer exist. Gated on a confirmed-loaded, non-error account set so we don't
        // wipe valid selections during the loading window (unifiedAccounts is
        // transiently empty) or on a transient fetch error (an errored fetch also
        // yields an empty unifiedAccounts, indistinguishable from "no accounts").
        useEffect(() => {
            if (isLoading || !onboardingProgress || accountsError) {
                return;
            }
            const liveAccountIds = new Set(unifiedAccounts.map((account) => String(account.id)));
            Object.keys(selections)
                .filter((accountId) => !liveAccountIds.has(accountId))
                .forEach((accountId) => clearAccountSelections(accountId));
        }, [isLoading, onboardingProgress, accountsError, unifiedAccounts, selections, clearAccountSelections]);

        // StepWizard2: Control navigation state based on account availability OR checkbox
        useEffect(() => {
            if (setCanProceed) {
                // Enable navigation when we have accounts to work with OR checkbox is checked
                const hasAccounts = unifiedAccounts.length > 0;
                const canProceed = hasAccounts || noTransactionsCheckbox;
                setCanProceed(canProceed);
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [unifiedAccounts.length, noTransactionsCheckbox]); // setCanProceed omitted to prevent infinite loop

        // V1 needs each account to have selections or a waiver before finishing.
        const unresolvedAccountIds = useMemo(() => {
            if (!isRedesignV1) {
                return new Set<string>();
            }
            return new Set(
                unifiedAccounts
                    .map((account) => String(account.id))
                    .filter((accountId) => !(selections[accountId]?.length ?? 0) && !waivedAccountIds.has(accountId))
            );
        }, [isRedesignV1, unifiedAccounts, selections, waivedAccountIds]);

        const canComplete = useMemo(() => {
            if (isRedesignV1 && unifiedAccounts.length > 0) {
                return unresolvedAccountIds.size < unifiedAccounts.length;
            }
            return totalSelectionCount > 0 || noTransactionsCheckbox;
        }, [isRedesignV1, unifiedAccounts.length, unresolvedAccountIds, totalSelectionCount, noTransactionsCheckbox]);

        const openConfirmationModal = useCallback(() => {
            if (unresolvedAccountIds.size > 0) {
                setShowUnresolved(true);
                return;
            }
            setModalState('showCompleteConfirmModal', true);
        }, [unresolvedAccountIds, setModalState]);

        useEffect(() => {
            onCanCompleteChange?.(canComplete);
        }, [canComplete, onCanCompleteChange]);

        // Helper function to find Hurdlr bank account ID from Plaid account ID (needed for handleComplete)
        const findHurdlrIdFromPlaidId = useCallback(
            (plaidId: string): number | null => {
                const foundAccount = getAccountById(plaidId);
                return foundAccount?.hurdlrBankAccountId || null;
            },
            [getAccountById]
        );

        // Navigation is now handled by StepWizard2

        // Mutations
        const completeStep = useCompleteOnboardingStep();
        const updateStep = useUpdateOnboardingStep();

        // Checkbox handler
        const handleNoTransactionsCheckbox = (event: React.ChangeEvent<HTMLInputElement>) => {
            const { checked } = event.target;
            setNoTransactionsCheckbox(checked);

            // Track no transactions checkbox selection
            sendTrackingEvent('No Transactions Checkbox Selected', {
                category: 'accounting_setup',
                action: 'no_transactions_selected',
                stepName: 'import_transactions',
                checked,
            });
        };

        useEffect(() => {
            setStoredWaivedAccountIds(WAIVED_ACCOUNTS_KEY, [...waivedAccountIds]);
        }, [waivedAccountIds, setStoredWaivedAccountIds]);

        const handleAccountWaivedChange = useCallback(
            (accountId: string, isWaived: boolean) => {
                setWaivedAccountIds((current) => {
                    const next = new Set(current);
                    if (isWaived) {
                        next.add(accountId);
                    } else {
                        next.delete(accountId);
                    }
                    return next;
                });
                sendTrackingEvent('No Transactions Checkbox Selected', {
                    category: 'accounting_setup',
                    action: 'no_transactions_selected',
                    stepName: 'import_transactions',
                    checked: isWaived,
                    accountId,
                });
            },
            [sendTrackingEvent]
        );

        const handleReviewAccount = useCallback(
            (account: UnifiedAccount) => {
                const originalAccount = getAccountById(String(account.id));
                if (originalAccount) {
                    setSelectedAccountForTransaction(originalAccount);
                    setModalState('showTransactionModal', true);
                }
            },
            [getAccountById, setSelectedAccountForTransaction, setModalState]
        );

        // Flags the accounts still open, so a refused exit says why rather than doing nothing.
        const isCompletionBlocked = useCallback((): boolean => {
            if (unresolvedAccountIds.size > 0) {
                setShowUnresolved(true);
                return true;
            }
            return !canComplete;
        }, [unresolvedAccountIds, canComplete]);

        const completeImport = useCallback(async (): Promise<boolean> => {
            if (isCompletionBlocked()) {
                return false; // Prevent navigation
            }

            setIsConfirming(true);
            setBackendError(null); // Clear any previous errors

            // --- Phase 1: Import and delete operations (backend-facing) ---
            let hasTransactionsToMove = false;
            let processedAccountIds: string[] = [];
            try {
                // Split accounts into those with and without selected transactions
                const accountsWithSelections: UnifiedAccount[] = [];
                const accountsWithoutSelections: UnifiedAccount[] = [];

                unifiedAccounts.forEach((account) => {
                    const accountTransactions = getAccountSelections(String(account.id));
                    if (accountTransactions.length > 0) {
                        accountsWithSelections.push(account);
                    } else {
                        accountsWithoutSelections.push(account);
                    }
                });

                hasTransactionsToMove = accountsWithSelections.length > 0;

                // Plaid IDs of accounts the user is dismissing without importing.
                // We piggyback these onto the FIRST /process-import call so the backend's
                // cleanup_personal_bank_plaid_connections durable task handles them too —
                // removes a redundant /cleanup-plaid/ round-trip and the page-reload risk
                // between /delete/ and /cleanup-plaid/. See PR #7592 review (hly-dev).
                const noSelectionPlaidIds = accountsWithoutSelections
                    .map((account) => String(account.id))
                    .filter(Boolean);

                // Accounts WITH transactions: dispatch async process-import (move + delete + cleanup)
                const importPromises = accountsWithSelections.map(async (account, idx) => {
                    const hurdlrId = findHurdlrIdFromPlaidId(String(account.id));
                    if (!hurdlrId) {
                        return { accountId: account.id, success: false, error: 'Could not find Hurdlr ID' };
                    }

                    const accountTransactions = getAccountSelections(String(account.id));
                    const transactionsData = accountTransactions.map((transaction) => ({
                        entity_id: transaction.entity_id,
                        entity_type: transaction.type || 'EXPENSE',
                    }));

                    // Attach no-selection IDs to exactly one batch (the first) so backend
                    // dispatches cleanup once, not N times.
                    const deletedAccountIds =
                        idx === 0 ? [String(account.id), ...noSelectionPlaidIds] : [String(account.id)];

                    const response = await collectiveApi.post('/bookkeeping/v2/personal-bank-import/process-import/', {
                        transactions: transactionsData,
                        hurdlr_bank_account_id: hurdlrId,
                        deleted_account_ids: deletedAccountIds,
                    });

                    if (!response.data?.success) {
                        return { accountId: account.id, success: false, error: 'process-import failed' };
                    }
                    return { accountId: account.id, success: true };
                });

                // Accounts WITHOUT transactions: delete + cleanup directly
                let deletePromises: Promise<{ accountId: string; success: boolean; error?: unknown }>[] = [];
                if (accountsWithSelections.length === 0) {
                    deletePromises = accountsWithoutSelections.map(async (account) => {
                        if (!account.id) {
                            return { accountId: account.id, success: false, error: 'No account ID' };
                        }

                        try {
                            const hurdlrId = findHurdlrIdFromPlaidId(String(account.id));
                            if (!hurdlrId) {
                                return { accountId: account.id, success: false, error: 'Could not find Hurdlr ID' };
                            }

                            await collectiveApi.post('/bookkeeping/personal-bank-account/delete/', {
                                hurdlr_bank_account_id: hurdlrId,
                            });
                            return { accountId: account.id, success: true };
                        } catch (deleteError) {
                            return { accountId: account.id, success: false, error: deleteError };
                        }
                    });
                }

                // Fire all import and delete operations in parallel
                const results = await Promise.allSettled([...importPromises, ...deletePromises]);

                // Check if any process-import calls failed (these are critical)
                const importFailures = results
                    .slice(0, importPromises.length)
                    .filter((r) => r.status === 'rejected' || (r.status === 'fulfilled' && !r.value.success));

                if (importFailures.length > 0) {
                    // Invalidate cache so successfully-processed accounts don't reappear on retry
                    await invalidateQueries();
                    setError('moveTransactions', 'Failed to process some account imports');
                    setBackendError('Failed to process some account imports');
                    setIsConfirming(false);
                    return false;
                }

                // Collect successfully-deleted account IDs for Plaid cleanup
                const deleteResults = results.slice(importPromises.length);
                const successfulDeleteIds = deleteResults
                    .filter((r) => r.status === 'fulfilled' && r.value.success)
                    .map((r) => String((r as PromiseFulfilledResult<{ accountId: string }>).value.accountId));

                // Cleanup Plaid only for successfully-deleted accounts (non-blocking).
                // Fallback path: if there were no /process-import calls (zero accounts with
                // transactions), the backend's durable cleanup task never fires, so we still
                // need this explicit call. When accountsWithSelections.length > 0, the no-
                // selection IDs were already attached to the first /process-import payload
                // and the backend will clean them up after the batch resolves.
                if (accountsWithSelections.length === 0 && successfulDeleteIds.length > 0) {
                    try {
                        await collectiveApi.post('/bookkeeping/personal-bank-account/cleanup-plaid/', {
                            deleted_account_ids: successfulDeleteIds,
                        });
                    } catch (cleanupError) {
                        // eslint-disable-next-line no-console
                        console.warn('Plaid cleanup request failed:', cleanupError);
                    }
                }

                // Build list of all successfully-processed account IDs
                // (imports that succeeded + deletes that succeeded)
                processedAccountIds = results
                    .filter((r) => r.status === 'fulfilled' && r.value.success)
                    .map((r) => String((r as PromiseFulfilledResult<{ accountId: string }>).value.accountId));
            } catch (error) {
                setIsConfirming(false);
                setBackendError(error instanceof Error ? error.message : 'Failed to process import');
                return false;
            }

            // --- Phase 2: Update onboarding step data (post-import bookkeeping) ---
            // Imports succeeded at this point — step update failures should not
            // tell the user "import failed" since their data is already processed.
            try {
                const currentStepData = stepData || {};
                const currentSourceAccounts = currentStepData.source_accounts || [];

                // Only strip successfully-processed accounts from step data
                const updatedSourceAccounts = currentSourceAccounts.filter(
                    (account: { id: string | number }) => !processedAccountIds.includes(account.id.toString())
                );

                const updatedStepData = {
                    ...currentStepData,
                    source_accounts: updatedSourceAccounts,
                };

                const bankConnectionData = onboardingProgress?.bank_connection_data;
                if (bankConnectionData?.confirmed_personal_accounts) {
                    const updatedPersonalAccounts = bankConnectionData.confirmed_personal_accounts.filter(
                        (account: { id: string | number }) => !processedAccountIds.includes(account.id.toString())
                    );

                    await updateStep.mutateAsync({
                        step: 'bank_connection',
                        status: 'completed',
                        data: {
                            ...bankConnectionData,
                            confirmed_personal_accounts: updatedPersonalAccounts,
                        },
                    });
                }

                const completionData = hasTransactionsToMove
                    ? updatedStepData
                    : {
                          imported_transactions: 0,
                          accounts_processed: processedAccountIds.length,
                          no_transactions: true,
                          ...updatedStepData,
                      };

                await completeStep.mutateAsync({
                    step: 'transaction_import',
                    data: completionData,
                });
            } catch (error) {
                // Imports already succeeded — don't block the user, but warn them
                // eslint-disable-next-line no-console
                console.warn('Step update failed after successful import:', error);
            }

            // --- Phase 3: Analytics and cleanup (never blocks the user) ---
            try {
                sendTrackingEvent('Categorization Section Completed', {
                    category: 'accounting_setup',
                    action: 'section_completed',
                    stepName: 'import_transactions',
                    transactionCount: totalSelectionCount,
                    totalNetExpenses,
                    selectedAccountIds: Object.keys(selections),
                    noTransactionsSelected: totalSelectionCount === 0,
                });
            } catch {
                // Analytics failure should never block the user
            }

            hasCompletedRef.current = true;
            clearAllSelections();
            setWaivedAccountIds(new Set());
            // Written here too, not only by the effect: navigating can unmount this step before it runs.
            setStoredWaivedAccountIds(WAIVED_ACCOUNTS_KEY, []);
            setModalState('showCompleteConfirmModal', false);
            setIsConfirming(false);

            if (onNavigateToNext) {
                onNavigateToNext();
            }

            return true;
        }, [
            isCompletionBlocked,
            setStoredWaivedAccountIds,
            completeStep,
            clearAllSelections,
            setModalState,
            onNavigateToNext,
            findHurdlrIdFromPlaidId,
            getAccountSelections,
            invalidateQueries,
            setError,
            stepData,
            sendTrackingEvent,
            totalSelectionCount,
            totalNetExpenses,
            selections,
            unifiedAccounts,
            onboardingProgress?.bank_connection_data,
            updateStep,
        ]);

        const handleComplete = useCallback(async (): Promise<boolean> => {
            if (hasCompletedRef.current) {
                return true;
            }
            if (isCompletingRef.current) {
                return false;
            }
            isCompletingRef.current = true;
            try {
                return await completeImport();
            } finally {
                isCompletingRef.current = false;
            }
        }, [completeImport]);

        const closeConfirmationModal = useCallback(
            () => setModalState('showCompleteConfirmModal', false),
            [setModalState]
        );

        const handleFinish = useCallback(async () => {
            await handleComplete();
        }, [handleComplete]);

        // Wrapper function for StepWizard2 onStepExit callback
        const handleStepExit = useCallback(async (): Promise<boolean> => {
            return handleComplete();
        }, [handleComplete]);

        // Expose methods to parent component for StepWizard2 customization
        useImperativeHandle(
            ref,
            () => ({
                handleStepExit,
                getTransactionCount: () => totalSelectionCount,
                hasSelections: () => hasSelections(),
                canComplete: () => canComplete,
                openConfirmationModal,
            }),
            [handleStepExit, totalSelectionCount, hasSelections, canComplete, openConfirmationModal]
        );

        // Show loading state
        if (isLoading) {
            return (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Typography variant="h6" gutterBottom>
                        Import Transactions
                    </Typography>
                    <CircularProgress />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                        Loading connected accounts...
                    </Typography>
                </Box>
            );
        }

        // Show error state
        if (accountsError) {
            return (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Typography variant="h6" gutterBottom>
                        Import Transactions
                    </Typography>
                    <Alert severity="error">Failed to load connected accounts. Please try again.</Alert>
                    {accountsError && 'details' in accountsError && accountsError.details && (
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                                Details: {accountsError.details.join(', ')}
                            </Typography>
                        </Box>
                    )}
                </Box>
            );
        }

        return (
            <Box
                sx={{
                    maxWidth: {
                        xs: '100%',
                        sm: theme.spacing(70), // 560px = 70 * 8px
                    },
                    width: '100%',
                    // Responsive spacing
                    px: { xs: 2, sm: 0 },
                    py: { xs: 1, sm: 0 },
                }}>
                <Stack spacing={0}>
                    {/* Header Section */}
                    <Stack spacing={0}>
                        <Typography
                            variant="h1"
                            color="text.primary"
                            sx={{
                                mb: 0,
                            }}>
                            Add business transactions from your personal accounts
                        </Typography>
                        <Typography variant="body1" color="text.secondary" mt={1.5}>
                            Find business expenses you paid personally since{' '}
                            <Typography component="span" variant="inherit" fontWeight="medium" color="text.primary">
                                {transactionStartDate ?? 'your accounting start date'}
                            </Typography>
                            . We&apos;ll make sure you don&apos;t miss expenses that could reduce your taxable income.
                        </Typography>
                        <Box sx={{ mt: 1 }}>
                            <WhyImportantLink
                                context={ACCOUNTING_SETUP_HELP_CONTEXT.importTransactions}
                                title="What business expenses should I look for?"
                                size="medium"
                                requiresSetupAgent
                            />
                        </Box>
                        <Typography variant="h4" color="text.primary" mt={5}>
                            Personal accounts to review
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            We&apos;ll disconnect each account when you&apos;re done.
                        </Typography>
                    </Stack>

                    {/* Accounts Section */}
                    <Stack spacing={{ xs: 1.5, sm: 2 }} sx={{ mt: { xs: 1.5, sm: 2 } }}>
                        {/* Validation warnings - non-blocking */}
                        {validationWarnings && validationWarnings.length > 0 && (
                            <Alert severity="warning">
                                <Typography variant="body2">
                                    Some account data has minor issues but the system is working normally:
                                </Typography>
                                <Box component="ul" sx={{ mt: 1, pl: 2 }}>
                                    {validationWarnings.slice(0, 3).map((warning) => (
                                        <li key={`warning-${warning.slice(0, 20)}-${warning.length}`}>
                                            <Typography variant="body2">{warning}</Typography>
                                        </li>
                                    ))}
                                    {validationWarnings.length > 3 && (
                                        <li>
                                            <Typography variant="body2">
                                                ... and {validationWarnings.length - 3} more warnings
                                            </Typography>
                                        </li>
                                    )}
                                </Box>
                            </Alert>
                        )}

                        {isRedesignV1 && unifiedAccounts.length > 0 ? (
                            unifiedAccounts.map((account) => (
                                <AccountReviewCard
                                    key={account.id}
                                    account={account}
                                    selectedCount={getAccountSelections(String(account.id)).length}
                                    isWaived={waivedAccountIds.has(String(account.id))}
                                    isUnresolved={showUnresolved && unresolvedAccountIds.has(String(account.id))}
                                    onReview={handleReviewAccount}
                                    onWaivedChange={handleAccountWaivedChange}
                                />
                            ))
                        ) : (
                            /* Custom bank account list matching Figma design */
                            <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                                {unifiedAccounts.length > 0 ? (
                                    <>
                                        {unifiedAccounts.map((account, index) => (
                                            <Box
                                                key={account.id}
                                                sx={{
                                                    width: '100%',
                                                    height: '100%',
                                                    // Responsive layout
                                                    padding: { xs: 2, sm: 3 },
                                                    borderBottom: index < unifiedAccounts.length - 1 ? 1 : 'none',
                                                    borderBottomColor: 'divider',
                                                    justifyContent: 'space-between',
                                                    alignItems: { xs: 'flex-start', sm: 'center' },
                                                    display: 'inline-flex',
                                                    // Mobile: Stack vertically, Desktop: Horizontal
                                                    flexDirection: { xs: 'column', sm: 'row' },
                                                    gap: { xs: 2, sm: 0 },
                                                }}>
                                                <AccountSummary account={account} />

                                                {/* Right side: Selected Count + Button */}
                                                <Box
                                                    sx={{
                                                        alignItems: 'center',
                                                        gap: 2,
                                                        display: 'flex',
                                                        // Responsive layout
                                                        width: { xs: '100%', sm: 'auto' },
                                                        justifyContent: { xs: 'space-between', sm: 'flex-start' },
                                                    }}>
                                                    {/* Selected Transaction Count */}
                                                    {getAccountSelections(String(account.id)).length > 0 && (
                                                        <Box
                                                            sx={{
                                                                height: 5, // 40px = 5 * 8px
                                                                paddingTop: 1.25, // 10px = 1.25 * 8px
                                                                paddingBottom: 1.25,
                                                                overflow: 'hidden',
                                                                borderRadius: 1.5, // 12px = 1.5 * 8px
                                                                justifyContent: 'center',
                                                                alignItems: 'center',
                                                                gap: 1,
                                                                display: 'flex',
                                                            }}>
                                                            <Box
                                                                sx={{
                                                                    flexDirection: 'column',
                                                                    justifyContent: 'flex-start',
                                                                    alignItems: 'flex-start',
                                                                    display: 'inline-flex',
                                                                }}>
                                                                <Typography
                                                                    variant="body2"
                                                                    color="text.secondary"
                                                                    fontWeight={500}>
                                                                    {getAccountSelections(String(account.id)).length}{' '}
                                                                    Selected
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                    )}

                                                    {/* Action Button */}
                                                    <Button
                                                        variant="contained"
                                                        color="secondary"
                                                        size="small"
                                                        onClick={() => handleReviewAccount(account)}
                                                        sx={{
                                                            // Responsive touch targets
                                                            width: { xs: theme.spacing(15), sm: theme.spacing(12.625) },
                                                            height: { xs: theme.spacing(5.5), sm: theme.spacing(4.75) },
                                                            minWidth: {
                                                                xs: theme.spacing(5.5),
                                                                sm: theme.spacing(4.75),
                                                            },
                                                            minHeight: {
                                                                xs: theme.spacing(5.5),
                                                                sm: theme.spacing(4.75),
                                                            },
                                                            borderRadius: theme.spacing(1.5),
                                                        }}>
                                                        Add
                                                    </Button>
                                                </Box>
                                            </Box>
                                        ))}
                                    </>
                                ) : (
                                    <Paper sx={{ p: 3 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <BankIcon
                                                sx={{
                                                    fontSize: theme.spacing(5), // 40px = 5 * 8px
                                                    color: 'primary.main',
                                                }}
                                            />
                                            <Stack>
                                                <Typography variant="body1" fontWeight={500}>
                                                    No personal accounts found
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Please go back to the bank connection step to classify your personal
                                                    accounts, or check the &quot;I don&apos;t have any business
                                                    transactions to add&quot; option below if you have no business
                                                    transactions.
                                                </Typography>
                                            </Stack>
                                        </Box>
                                    </Paper>
                                )}
                            </Paper>
                        )}
                    </Stack>

                    {/* Summary Section */}
                    <Stack spacing={2} sx={{ mt: { xs: 3, sm: 5 } }}>
                        <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                            <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                sx={{
                                    // Responsive layout
                                    '& > *': {
                                        flex: { xs: 'none', sm: 1 },
                                        borderBottom: { xs: 1, sm: 'none' },
                                        '&:not(:last-child)': {
                                            borderRight: { xs: 'none', sm: 1 },
                                            borderRightColor: { xs: 'transparent', sm: 'divider' },
                                        },
                                        '&:last-child': {
                                            borderBottom: { xs: 'none', sm: 'none' },
                                        },
                                    },
                                }}>
                                <Box
                                    sx={{
                                        flex: 1,
                                        p: { xs: 2, sm: 2.5 },
                                        borderBottomLeftRadius: 1,
                                    }}>
                                    <Stack spacing={0.5}>
                                        <Typography variant="caption" color="text.secondary">
                                            Transactions added
                                        </Typography>
                                        <Typography variant="h4" color="text.primary">
                                            {totalSelectionCount}
                                        </Typography>
                                    </Stack>
                                </Box>
                                <Box
                                    sx={{
                                        flex: 1,
                                        p: { xs: 2, sm: 2.5 },
                                    }}>
                                    <Stack spacing={0.5}>
                                        <Typography variant="caption" color="text.secondary">
                                            Net expenses
                                        </Typography>
                                        <Typography variant="h4" color="text.primary">
                                            {moneyFormat(totalNetExpenses, { minPrecision: 2 })}
                                        </Typography>
                                    </Stack>
                                </Box>
                                <Box
                                    sx={{
                                        flex: 1,
                                        p: { xs: 2, sm: 2.5 },
                                        borderBottomRightRadius: 1,
                                    }}>
                                    <Stack spacing={0.5}>
                                        <Typography variant="caption" color="text.secondary">
                                            Est. federal tax savings*
                                        </Typography>
                                        <Typography variant="h4" color="success.main">
                                            {moneyFormat(estimatedDeductions, { minPrecision: 2 })}
                                        </Typography>
                                    </Stack>
                                </Box>
                            </Stack>
                        </Paper>
                        <Typography variant="caption" color="text.secondary">
                            *Estimated deductions are based on typical federal tax rates of 22%. Your actual savings may
                            vary depending on your total income, other deductions and credits, payroll setup, and
                            specific tax situation.
                        </Typography>
                    </Stack>

                    {/* Checkbox for no transactions */}
                    {totalSelectionCount === 0 && !(isRedesignV1 && unifiedAccounts.length > 0) && (
                        <Box sx={{ mt: 3 }}>
                            <NoTransactionsCheckbox
                                checked={noTransactionsCheckbox}
                                onChange={handleNoTransactionsCheckbox}
                            />
                        </Box>
                    )}
                </Stack>

                {/* Transaction Selection Modal */}
                <TransactionSelectionModal
                    open={modalState.showTransactionModal}
                    onClose={() => {
                        setModalState('showTransactionModal', false);
                    }}
                    account={state.selectedAccountForTransaction}
                    // Pass the shared state and functions from the parent
                    setAccountSelections={setAccountSelections}
                    getAccountSelections={getAccountSelections}
                />

                <ConfirmationDialog
                    open={modalState.showCompleteConfirmModal}
                    handleClose={isConfirming ? undefined : closeConfirmationModal}
                    title="Done reviewing your personal accounts?"
                    message={
                        <>
                            <DialogContentText variant="body1" color="text.primary">
                                Make sure you&apos;ve added all your business transactions.{' '}
                                <Typography component="span" variant="bodySemiBold">
                                    Once you&apos;re done, your personal accounts will be automatically disconnected.
                                </Typography>
                            </DialogContentText>
                            <DialogContentText variant="body1" color="text.primary">
                                Going forward, you can add business expenses you paid personally through{' '}
                                <Typography component="span" variant="bodySemiBold">
                                    Reimbursements
                                </Typography>
                                .
                            </DialogContentText>
                            {backendError && <Alert severity="error">{backendError}</Alert>}
                        </>
                    }
                    reject={{
                        label: 'Go back',
                        variant: 'text',
                        onClick: closeConfirmationModal,
                        disabled: isConfirming,
                    }}
                    accept={{ label: 'Finish', onClick: handleFinish, loading: isConfirming }}
                />
            </Box>
        );
    }
);

ImportTransactions.displayName = 'ImportTransactions';

// Default props for optional properties
ImportTransactions.defaultProps = {
    onboardingProgress: undefined,
    onCanCompleteChange: undefined,
    onNavigateToNext: undefined,
};

export default ImportTransactions;
